import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, Receipt } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import type { Expense, Organization } from '@/lib/types';
import { EXPENSE_LABELS } from '@/lib/types';
import { useAuth } from '@/lib/auth';
import { CrudActions } from '@/components/ui/CrudActions';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Badge, Button, Card, Input, Spinner, Textarea } from '@/components/ui';

const emptyForm = {
  expenseDate: new Date().toISOString().slice(0, 10),
  amount: '',
  description: '',
  category: 'food',
};

export function ExpensesPage() {
  const { user, effectiveIsSuperAdmin, viewAsUser } = useAuth();
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadExpenseId, setUploadExpenseId] = useState<string | null>(null);

  const { data: orgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api<{ organizations: Organization[] }>('/organizations'),
  });

  const orgId = orgs?.organizations[0]?.id;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['expenses', orgId],
    queryFn: () => api<{ expenses: Expense[] }>(`/expenses/org/${orgId}`),
    enabled: !!orgId,
  });

  const { data: reports } = useQuery({
    queryKey: ['expense-reports', orgId],
    queryFn: () =>
      api<{ total: number; byCategory: { category: string; total: number }[] }>(
        `/expenses/org/${orgId}/reports`,
      ),
    enabled: !!orgId && effectiveIsSuperAdmin,
  });

  const expenses = (data?.expenses ?? []).filter((expense) => {
    if (effectiveIsSuperAdmin && !viewAsUser) return true;
    return expense.userId === user?.id;
  });

  function canModify(expense: Expense) {
    if (effectiveIsSuperAdmin) return true;
    return expense.userId === user?.id;
  }

  function resetForm() {
    setForm({ ...emptyForm, expenseDate: new Date().toISOString().slice(0, 10) });
    setEditingId(null);
    setShowCreate(false);
    setError('');
  }

  function startEdit(expense: Expense) {
    setEditingId(expense.id);
    setShowCreate(false);
    setForm({
      expenseDate: expense.expenseDate,
      amount: String(expense.amount),
      description: expense.description,
      category: expense.category,
    });
  }

  async function saveExpense(e: React.FormEvent) {
    e.preventDefault();
    if (!orgId) return;
    setLoading(true);
    setError('');
    try {
      const payload = {
        expenseDate: form.expenseDate,
        amount: parseFloat(form.amount),
        description: form.description,
        category: form.category,
      };
      if (editingId) {
        await api(`/expenses/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await api(`/expenses/org/${orgId}`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      resetForm();
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    } finally {
      setLoading(false);
    }
  }

  async function deleteExpense(expenseId: string) {
    if (!confirm('Bu harcamayı silmek istediğinize emin misiniz?')) return;
    await api(`/expenses/${expenseId}`, { method: 'DELETE' });
    if (editingId === expenseId) resetForm();
    await refetch();
  }

  async function uploadReceipt(expenseId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    await api(`/expenses/${expenseId}/receipt`, {
      method: 'POST',
      body: formData,
    });
    setUploadExpenseId(null);
    await refetch();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Harcamalar</h2>
          {viewAsUser && user?.isSuperAdmin && (
            <p className="text-xs text-muted-foreground">Kullanıcı görünümü — sadece kendi fişleriniz</p>
          )}
        </div>
        <Button
          size="sm"
          onClick={() => {
            resetForm();
            setShowCreate(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Ekle
        </Button>
      </div>

      {reports && effectiveIsSuperAdmin && (
        <Card>
          <p className="text-sm text-muted-foreground">Toplam Harcama (Tüm Grup)</p>
          <p className="text-2xl font-bold text-primary">{formatCurrency(reports.total)}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {reports.byCategory?.map((cat) => (
              <Badge key={cat.category}>
                {EXPENSE_LABELS[cat.category] ?? cat.category}: {formatCurrency(cat.total)}
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {(showCreate || editingId) && (
        <Card>
          <form onSubmit={saveExpense} className="space-y-3">
            <p className="font-serif text-sm font-semibold text-primary">
              {editingId ? 'Harcama Düzenle' : 'Yeni Harcama'}
            </p>
            <Input
              type="date"
              value={form.expenseDate}
              onChange={(e) => setForm({ ...form, expenseDate: e.target.value })}
              required
            />
            <Input
              type="number"
              step="0.01"
              placeholder="Tutar (₺)"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              required
            />
            <Textarea
              placeholder="Açıklama"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
            />
            <select
              className="flex h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            >
              {Object.entries(EXPENSE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={loading}>
                {loading ? <Spinner /> : editingId ? 'Kaydet' : 'Ekle'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>
                İptal
              </Button>
            </div>
          </form>
        </Card>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner className="h-8 w-8" />
        </div>
      ) : expenses.length === 0 ? (
        <Card className="text-center text-sm text-muted-foreground">Henüz harcama kaydı yok</Card>
      ) : (
        expenses.map((expense) => (
          <Card key={expense.id}>
            {editingId === expense.id ? null : (
              <>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold">{formatCurrency(expense.amount)}</p>
                    <p className="text-sm">{expense.description}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {formatDate(expense.expenseDate)}
                      {expense.userName && ` • ${expense.userName}`}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge>{EXPENSE_LABELS[expense.category] ?? expense.category}</Badge>
                    {canModify(expense) && (
                      <CrudActions
                        onEdit={() => startEdit(expense)}
                        onDelete={() => deleteExpense(expense.id)}
                      />
                    )}
                  </div>
                </div>
                {expense.receipts && expense.receipts.length > 0 ? (
                  <div className="mt-3 flex gap-2">
                    {expense.receipts.map((r) => (
                      <a
                        key={r.id}
                        href={r.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-primary underline"
                      >
                        <Receipt className="h-3 w-3" />
                        Fiş
                      </a>
                    ))}
                  </div>
                ) : canModify(expense) ? (
                  <div className="mt-3">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*,application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && uploadExpenseId) uploadReceipt(uploadExpenseId, file);
                      }}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setUploadExpenseId(expense.id);
                        fileRef.current?.click();
                      }}
                    >
                      <Receipt className="h-4 w-4" />
                      Fiş Yükle
                    </Button>
                  </div>
                ) : null}
              </>
            )}
          </Card>
        ))
      )}
    </div>
  );
}
